import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Chip,
  Button,
} from '@mui/material';
import { CodeOutlined } from '@mui/icons-material';

import { DrawerHeader } from '@shared/components/drawer';
import { alpha } from '@mui/material/styles';
import StatusChip from '@shared/components/StatusChip/StatusChip';
import NoDataFound from '@shared/components/NoDataFound/NoDataFound';
import JsonViewerDialog from '@shared/components/JsonViewerDialog/JsonViewerDialog';
import { useOfferSyncLogs } from '@modules/offers/hooks';
import { formatDate } from '@modules/offers/utils';
import { LoadingSpinner } from '@shared/components';

interface SyncLogsDialogProps {
  open: boolean;
  offerId?: number | string;
  onClose: () => void;
}

export const SyncLogsDialog: React.FC<SyncLogsDialogProps> = ({ open, offerId, onClose }) => {
  const theme = useTheme();
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState<Record<string, any> | null>(null);
  // Only fetch when the dialog is open to avoid API calls during list render
  const { data: logs, isLoading, refetch, isError } = useOfferSyncLogs(open ? offerId : undefined);

  const getEnvironmentColor = (env: 'staging' | 'prod') => {
    return env === 'prod' ? 'success' : 'primary';
  };

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [open]);

  const handleViewPayload = (payload: Record<string, any>) => {
    setSelectedPayload(payload);
    setJsonViewerOpen(true);
  };

  const handleCloseJsonViewer = () => {
    setJsonViewerOpen(false);
    setSelectedPayload(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DrawerHeader title="Sync Logs" onClose={onClose} />
      <DialogContent sx={{ pt: 1 }}>
        {isLoading && <LoadingSpinner fullHeight message="Fetching sync logs..." />}
        {!isLoading && isError && (
          <Typography color="error" variant="body2">
            Failed to load sync logs.
          </Typography>
        )}
        {!isLoading && !isError && (
          <Box>
            {!logs || logs.length === 0 ? (
              <NoDataFound message="No sync logs found." />
            ) : (
              <TableContainer sx={{ maxHeight: '65vh', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
                <Table stickyHeader size="small" sx={{ '& th, & td': { py: 1.25 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: 170, top: 0, backgroundColor: 'background.paper' }}>
                        Synced On
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140, top: 0, backgroundColor: 'background.paper' }}>
                        Environment
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 140, top: 0, backgroundColor: 'background.paper' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, top: 0, backgroundColor: 'background.paper' }}>
                        Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 170, top: 0, backgroundColor: 'background.paper' }}>
                        Request Payload
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => {
                      const isSuccess = log.sync_status === 'success';
                      const leftColor = isSuccess ? theme.palette.success.main : theme.palette.error.main;
                      return (
                        <TableRow
                          key={log.sync_log_id}
                          hover
                          sx={{
                            borderLeft: `4px solid ${leftColor}`,
                            '&:last-child td, &:last-child th': { borderBottom: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatDate(log.created_at)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {new Date(log.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={log.sync_on.toUpperCase()}
                              size="small"
                              color={getEnvironmentColor(log.sync_on)}
                              variant="outlined"
                              sx={{ fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              label={log.sync_status}
                              size="small"
                              status={log.sync_status || 'success'}
                              sx={{ fontWeight: 700 }}
                              useBoxStyle
                              showIcon
                            />
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {isSuccess ? (
                              <Box
                                sx={{
                                  display: 'inline-block',
                                  px: 1.25,
                                  py: 0.75,
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.success.main, 0.08),
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                  color: 'success.dark',
                                }}
                              >
                                <Typography variant="body2">
                                  {log.response_message || 'Offer successfully pushed.'}
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  px: 1.25,
                                  py: 0.9,
                                  borderRadius: 1,
                                  bgcolor: alpha(theme.palette.error.main, 0.08),
                                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                  color: 'error.dark',
                                }}
                              >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {log.error_details || 'Sync failed'}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {log.request_payload && Object.keys(log.request_payload).length > 0 ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewPayload(log.request_payload)}
                                startIcon={<CodeOutlined />}
                                sx={{ minWidth: 'auto', px: 1.5 }}
                              >
                                View
                              </Button>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      {jsonViewerOpen && (
        <JsonViewerDialog
          open={jsonViewerOpen}
          onClose={handleCloseJsonViewer}
          data={selectedPayload}
          title="Request Payload"
        />
      )}
    </Dialog>
  );
};

export default SyncLogsDialog;
